import type {
  IConfluencePage,
  IConfluencePageListParams,
  IConfluenceSpace,
  IConnectIntegrationPayload,
  IDeleteIntegrationConnectionPayload,
  IDriveFile,
  IEnabledIntegrationsResponse,
  IFileListParams,
  IIntegrationConfigs,
  IIntegrationConnectResponse,
  IIntegrationTestResponse,
  IMcpApprovalTarget,
  INextcloudFileListParams,
  INextcloudLoginFlowStatusResponse,
  IStartNextcloudLoginFlowResponse,
  ITelegramAuthResponse,
  ITelegramSendCodePayload,
  ITelegramVerifyCodePayload,
  ITelegramVerifyPasswordPayload,
  ITestIntegrationConnectionPayload,
  TParsedIntegrations,
} from "../types";
import type { ApiClient } from "./client";

export class IntegrationApi {
  constructor(private apiClient: ApiClient) {}

  async getAll(): Promise<TParsedIntegrations> {
    const response =
      await this.apiClient.get<TParsedIntegrations>("/integration/all");
    return response.data;
  }

  async getConfigs(): Promise<IIntegrationConfigs> {
    const response = await this.apiClient.get<IIntegrationConfigs>(
      "/integration/configs",
    );
    return response.data;
  }

  async connect(
    data: IConnectIntegrationPayload,
  ): Promise<IIntegrationConnectResponse> {
    const response = await this.apiClient.post<IIntegrationConnectResponse>(
      "/integration/access",
      data,
    );
    return response.data;
  }

  async deleteConnection({
    integrationUuid,
    provider,
  }: IDeleteIntegrationConnectionPayload): Promise<void> {
    await this.apiClient.delete(`/integration/${integrationUuid}`, {
      data: { provider },
    });
  }

  async testConnection({
    integrationUuid,
    provider,
  }: ITestIntegrationConnectionPayload): Promise<IIntegrationTestResponse> {
    const response = await this.apiClient.post<IIntegrationTestResponse>(
      `/integration/test/${integrationUuid}`,
      {
        provider,
      },
    );
    return response.data;
  }

  async getEnabledIntegrations(): Promise<IEnabledIntegrationsResponse> {
    const response = await this.apiClient.get<IEnabledIntegrationsResponse>(
      "/integration/enabled",
    );
    return response.data;
  }

  async getConfluenceSpaces(): Promise<IConfluenceSpace[]> {
    const response = await this.apiClient.get<IConfluenceSpace[]>(
      "/integration/atlassian/confluence/spaces",
    );
    return response.data;
  }

  async getConfluencePageList(
    params?: IConfluencePageListParams,
  ): Promise<IConfluencePage[]> {
    const response = await this.apiClient.get<IConfluencePage[]>(
      "/integration/atlassian/confluence/page-list",
      { params },
    );
    return response.data;
  }

  async getGoogleDriveFiles(params?: IFileListParams): Promise<IDriveFile[]> {
    const response = await this.apiClient.get<IDriveFile[]>(
      "/integration/google/drive/file-list",
      { params },
    );
    return response.data;
  }

  async getOneDriveFiles(params?: IFileListParams): Promise<IDriveFile[]> {
    const response = await this.apiClient.get<IDriveFile[]>(
      "/integration/azure/onedrive/file-list",
      { params },
    );
    return response.data;
  }

  async getDropboxFiles(params?: IFileListParams): Promise<IDriveFile[]> {
    const response = await this.apiClient.get<IDriveFile[]>(
      "/integration/dropbox/file-list",
      { params },
    );
    return response.data;
  }

  // NOTE: `path` is the folder being opened. WebDAV is directory-scoped, so
  // unlike the other drive connectors this lists one level at a time.
  async getNextcloudFiles(
    params?: INextcloudFileListParams,
  ): Promise<IDriveFile[]> {
    const response = await this.apiClient.get<IDriveFile[]>(
      "/integration/nextcloud/file-list",
      { params },
    );
    return response.data;
  }

  async startNextcloudLoginFlow(): Promise<IStartNextcloudLoginFlowResponse> {
    const response =
      await this.apiClient.post<IStartNextcloudLoginFlowResponse>(
        "/integration/nextcloud/login-flow/start",
        {},
      );
    return response.data;
  }

  // NOTE: Polled until the status stops being PENDING. The handle is opaque —
  // the Nextcloud poll token stays server-side.
  async getNextcloudLoginFlowStatus(
    handle: string,
  ): Promise<INextcloudLoginFlowStatusResponse> {
    const response =
      await this.apiClient.get<INextcloudLoginFlowStatusResponse>(
        "/integration/nextcloud/login-flow/status",
        { params: { handle } },
      );
    return response.data;
  }

  // NOTE: NSAI-4328 — the three Telegram login steps. Deliberately NOT routed
  // through `connect()`: /integration/access answers a Telegram connect with the
  // caller's own redirect URL, which is a page for web but the app's deep link on
  // mobile — opening it there sends the app away mid-login.
  async telegramSendCode(
    data: ITelegramSendCodePayload,
  ): Promise<ITelegramAuthResponse> {
    const response = await this.apiClient.post<ITelegramAuthResponse>(
      "/integration/telegram/send-code",
      data,
    );
    return response.data;
  }

  async telegramVerifyCode(
    data: ITelegramVerifyCodePayload,
  ): Promise<ITelegramAuthResponse> {
    const response = await this.apiClient.post<ITelegramAuthResponse>(
      "/integration/telegram/verify-code",
      data,
    );
    return response.data;
  }

  async telegramVerifyPassword(
    data: ITelegramVerifyPasswordPayload,
  ): Promise<ITelegramAuthResponse> {
    const response = await this.apiClient.post<ITelegramAuthResponse>(
      "/integration/telegram/verify-password",
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-5354 — the member's connected MCP servers with the tools each one
  // currently advertises. Discovery is live (one handshake per server), so treat this as
  // best-effort: an unreachable server comes back with `isUnavailable: true` and no
  // actions rather than failing the whole call.
  async getMcpApprovalTargets(): Promise<IMcpApprovalTarget[]> {
    const response = await this.apiClient.get<IMcpApprovalTarget[]>(
      "/integration/mcps/approval-targets",
    );
    return response.data;
  }
}
